import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@incubs/ui';
import { useSchoolTheme, type BgStyle, type BgIntensity } from '../../hooks/useSchoolTheme';

const COLLAPSED = 72;
const EXPANDED  = 264;

const navItems = [
  { to: '/dashboard',  label: 'Dashboard', icon: '⌂', end: true  },
  { to: '/discover',   label: 'Discover',  icon: '✦', end: false },
  { to: '/matches',    label: 'Matches',   icon: '♡', end: false },
  { to: '/chat',       label: 'Messages',  icon: '◻', end: false },
  { to: '/startups',   label: 'Startups',  icon: '◈', end: false },
  { to: '/incubator',  label: 'Incubator', icon: '⟡', end: false },
];

const profileItems = [
  { to: '/profile', label: 'Profile', icon: '◎', end: false },
  { to: '/admin',   label: 'Admin',   icon: '⚙', end: false },
];

export default function Sidebar() {
  const [expanded, setExpanded]     = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const { mode }                     = useTheme();
  const { theme, updateTheme, allThemes, bgStyle, updateBgStyle, bgIntensity, updateBgIntensity } = useSchoolTheme();

  const $light    = mode === 'light';
  const primary   = theme.primary;
  const secondary = theme.secondary;

  // ── Colour tokens derived from school theme ──────────────────────────────
  const bg          = $light ? 'rgba(255,255,255,0.96)' : 'rgba(4,4,14,0.86)';
  const borderClr   = `${primary}20`;
  const shadow      = $light
    ? `4px 0 32px ${primary}14`
    : `4px 0 40px rgba(0,0,0,0.40), 2px 0 0 ${primary}18`;
  const activeBg    = `${primary}22`;
  const hoverBg     = `${primary}12`;
  const activeClr   = primary;
  const inactiveClr = $light ? 'rgba(30,10,76,0.35)' : 'rgba(255,255,255,0.30)';
  const labelClr    = $light ? '#1e0a4c'              : 'rgba(255,255,255,0.82)';
  const dividerClr  = `${primary}18`;

  // ── Nav item renderer ─────────────────────────────────────────────────────
  function renderItem(item: (typeof navItems)[number]) {
    const isHovered = hoveredItem === item.to;

    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        style={{ textDecoration: 'none', display: 'block', width: '100%' }}
      >
        {({ isActive }) => (
          <motion.div
            whileHover={{ x: 2 }}
            whileTap={{ scale: 0.96 }}
            onHoverStart={() => setHoveredItem(item.to)}
            onHoverEnd={() => setHoveredItem(null)}
            style={{
              position: 'relative',
              width: '100%',
              height: 44,
              borderRadius: 12,
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0 12px',
              background: isActive ? activeBg : isHovered ? hoverBg : 'transparent',
              color: isActive ? activeClr : inactiveClr,
              cursor: 'pointer',
              transition: 'background 180ms ease, color 180ms ease',
              overflow: 'hidden',
            }}
          >
            {/* Sliding active indicator — uses school gradient */}
            {isActive && (
              <motion.div
                layoutId="activeBar"
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 10,
                  width: 3,
                  height: 24,
                  borderRadius: '0 3px 3px 0',
                  background: `linear-gradient(180deg, ${primary}, ${secondary})`,
                }}
                transition={{ type: 'spring', stiffness: 400, damping: 35 }}
              />
            )}

            {/* Icon */}
            <span
              style={{
                fontSize: '1.175rem',
                flexShrink: 0,
                width: 24,
                textAlign: 'center',
                color: isActive ? activeClr : inactiveClr,
                transition: 'color 180ms ease',
              }}
            >
              {item.icon}
            </span>

            {/* Label — fades in when expanded */}
            <AnimatePresence>
              {expanded && (
                <motion.span
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -6 }}
                  transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: '1.05rem',
                    fontWeight: isActive ? 600 : 400,
                    fontStyle: 'italic',
                    color: isActive ? activeClr : labelClr,
                    whiteSpace: 'nowrap',
                    letterSpacing: '0.01em',
                  }}
                >
                  {item.label}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </NavLink>
    );
  }

  return (
    <motion.nav
      onHoverStart={() => setExpanded(true)}
      onHoverEnd={() => setExpanded(false)}
      animate={{ width: expanded ? EXPANDED : COLLAPSED }}
      transition={{ type: 'spring', stiffness: 380, damping: 36 }}
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: '1.25rem 0 1rem',
        background: bg,
        backdropFilter: 'blur(28px) saturate(200%)',
        WebkitBackdropFilter: 'blur(28px) saturate(200%)',
        borderRight: `1px solid ${borderClr}`,
        boxShadow: shadow,
        zIndex: 200,
        overflowX: 'hidden',
        overflowY: 'hidden',
        transition: 'box-shadow 400ms ease, border-color 400ms ease',
      }}
    >
      {/* ── Logo mark ─────────────────────────────────────────────────── */}
      <div style={{ padding: '0 16px', marginBottom: '1.5rem', flexShrink: 0 }}>
        <NavLink to="/" style={{ textDecoration: 'none' }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: `linear-gradient(135deg, ${primary}, ${secondary})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 600,
            fontStyle: 'italic',
            fontSize: '1.25rem',
            color: 'white',
            letterSpacing: '0em',
            boxShadow: `0 0 20px ${primary}50`,
            flexShrink: 0,
            cursor: 'pointer',
            transition: 'background 400ms ease, box-shadow 400ms ease',
          }}
        >
          I
        </div>
        </NavLink>

        {/* School name badge — fades in when expanded */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.15 }}
              style={{
                marginTop: '0.5rem',
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: '0.75rem',
                fontWeight: 400,
                fontStyle: 'italic',
                letterSpacing: '0.06em',
                color: primary,
                whiteSpace: 'nowrap',
                transition: 'color 400ms ease',
              }}
            >
              {theme.emoji} {theme.short}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Primary nav ───────────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.125rem',
          width: '100%',
          flex: 1,
          padding: '0 12px',
        }}
      >
        {navItems.map(renderItem)}
      </div>

      {/* ── Customise panel (expanded only) ───────────────────────────── */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            style={{
              width: '100%',
              borderTop: `1px solid ${dividerClr}`,
              borderBottom: `1px solid ${dividerClr}`,
              marginBottom: '0.25rem',
              /* Let this section scroll if content overflows the sidebar */
              overflowY: 'auto',
              maxHeight: '220px',
              padding: '0.75rem 12px 0.875rem',
            }}
          >

            {/* ── Section label ──────────────────────────────────────── */}
            <div style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: '0.875rem', fontWeight: 500, fontStyle: 'italic', letterSpacing: '0.06em',
              color: primary, opacity: 0.85,
              marginBottom: '0.625rem', paddingLeft: 1,
            }}>
              Customise
            </div>

            {/* ── School color swatches ──────────────────────────────── */}
            <div style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: '0.775rem', fontWeight: 400, fontStyle: 'italic', letterSpacing: '0.05em',
              color: inactiveClr,
              marginBottom: '0.4rem', paddingLeft: 1,
            }}>
              Affiliation
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px', marginBottom: '0.75rem' }}>
              {allThemes.map((t) => {
                const isSelected = theme.id === t.id;
                return (
                  <motion.button
                    key={t.id}
                    onClick={() => updateTheme(t.id)}
                    title={t.name}
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.88 }}
                    animate={isSelected ? { scale: 1.22 } : { scale: 1 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 28 }}
                    style={{
                      width: 22, height: 22, borderRadius: '50%',
                      background: `linear-gradient(135deg, ${t.primary}, ${t.secondary ?? t.primary})`,
                      border: isSelected ? `2.5px solid ${$light ? primary : 'rgba(255,255,255,0.95)'}` : `2px solid ${$light ? primary + '30' : 'rgba(255,255,255,0.10)'}`,
                      outline: 'none', cursor: 'pointer', padding: 0, flexShrink: 0,
                      boxShadow: isSelected
                        ? `0 0 0 3px ${t.primary}55, 0 0 18px ${t.primary}99`
                        : `0 2px 6px rgba(0,0,0,0.3)`,
                      position: 'relative',
                    }}
                  >
                    {isSelected && (
                      <span style={{
                        position: 'absolute', inset: 0, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.5rem', color: 'white',
                        textShadow: '0 1px 3px rgba(0,0,0,0.8)', fontWeight: 900,
                      }}>✓</span>
                    )}
                  </motion.button>
                );
              })}
            </div>

            {/* ── Background style ───────────────────────────────────── */}
            <div style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: '0.775rem', fontWeight: 400, fontStyle: 'italic', letterSpacing: '0.05em',
              color: inactiveClr,
              marginBottom: '0.4rem', paddingLeft: 1,
            }}>
              Background
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '5px', marginBottom: '0.75rem' }}>
              {([
                {
                  id: 'orbs', label: 'Velours',
                  /* Two velvet colour pools */
                  css: { background: `radial-gradient(ellipse at 30% 30%, ${primary} 0%, transparent 55%), radial-gradient(ellipse at 75% 75%, ${secondary} 0%, transparent 55%), #050403` } as React.CSSProperties,
                },
                {
                  id: 'gradient', label: 'Dégradé',
                  css: { background: `linear-gradient(142deg, ${primary} 0%, #050403 50%, ${secondary} 100%)` } as React.CSSProperties,
                },
                {
                  id: 'aurora', label: 'Aurore',
                  css: { background: `linear-gradient(-45deg, ${primary}, rgba(201,168,76,0.7), ${secondary}, #050403, ${primary})` } as React.CSSProperties,
                },
                {
                  id: 'mesh', label: 'Damier',
                  /* Checkered luxury grid preview */
                  css: {
                    backgroundImage: `linear-gradient(45deg, ${primary} 25%, transparent 25%), linear-gradient(-45deg, ${primary} 25%, transparent 25%), linear-gradient(45deg, transparent 75%, ${primary} 75%), linear-gradient(-45deg, transparent 75%, ${primary} 75%)`,
                    backgroundSize: '10px 10px',
                    backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px',
                    backgroundColor: '#050403',
                  } as React.CSSProperties,
                },
                {
                  id: 'dots', label: 'Quilté',
                  /* Diamond grid preview */
                  css: {
                    backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 4px, ${primary} 4px, ${primary} 5px), repeating-linear-gradient(-45deg, transparent, transparent 4px, ${primary} 5px, ${primary} 6px)`,
                    backgroundColor: '#050403',
                  } as React.CSSProperties,
                },
                {
                  id: 'waves', label: 'Rayé',
                  /* Pinstripe preview */
                  css: {
                    background: `repeating-linear-gradient(-62deg, transparent 0px, transparent 2px, ${primary} 2px, ${primary} 3px, transparent 3px, transparent 6px, ${secondary} 6px, ${secondary} 7px, transparent 7px, transparent 10px)`,
                    backgroundColor: '#050403',
                  } as React.CSSProperties,
                },
                {
                  id: 'minimal', label: 'Noir',
                  css: { background: '#050403' } as React.CSSProperties,
                },
              ] as { id: BgStyle; label: string; css: React.CSSProperties }[]).map(opt => {
                const isOn = bgStyle === opt.id;
                return (
                  <motion.button
                    key={opt.id}
                    onClick={() => updateBgStyle(opt.id)}
                    title={opt.label}
                    whileHover={{ scale: 1.06, y: -1 }}
                    whileTap={{ scale: 0.93 }}
                    animate={isOn ? { scale: 1.04 } : { scale: 1 }}
                    transition={{ type: 'spring', stiffness: 450, damping: 26 }}
                    style={{
                      height: 44, borderRadius: 9, cursor: 'pointer', outline: 'none', padding: 0,
                      ...opt.css,
                      border: isOn
                        ? `2.5px solid rgba(255,255,255,0.92)`
                        : '2px solid rgba(255,255,255,0.10)',
                      boxShadow: isOn
                        ? `0 0 0 3px ${primary}55, 0 4px 18px ${primary}66`
                        : '0 2px 8px rgba(0,0,0,0.5)',
                      position: 'relative', overflow: 'hidden',
                    }}
                  >
                    {/* Top-left gloss sheen */}
                    <div style={{
                      position: 'absolute', inset: 0,
                      background: 'linear-gradient(145deg, rgba(255,255,255,0.14) 0%, transparent 55%)',
                      borderRadius: 7, pointerEvents: 'none',
                    }} />
                    <div style={{
                      position: 'absolute', inset: 0,
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', gap: 1,
                    }}>
                      <span style={{
                        fontSize: '0.44rem', fontWeight: 800, letterSpacing: '0.05em',
                        color: 'rgba(255,255,255,0.90)',
                        textShadow: '0 1px 6px rgba(0,0,0,1), 0 0 12px rgba(0,0,0,0.8)',
                        lineHeight: 1,
                      }}>{opt.label.toUpperCase()}</span>
                    </div>
                    {isOn && (
                      <motion.div
                        layoutId="bgActiveRing"
                        style={{
                          position: 'absolute', inset: -1,
                          borderRadius: 10,
                          border: `2px solid rgba(255,255,255,0.50)`,
                          pointerEvents: 'none',
                        }}
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                      />
                    )}
                  </motion.button>
                );
              })}
            </div>

            {/* ── Intensity ──────────────────────────────────────────── */}
            <div style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: '0.775rem', fontWeight: 400, fontStyle: 'italic', letterSpacing: '0.05em',
              color: inactiveClr,
              marginBottom: '0.4rem', paddingLeft: 1,
            }}>
              Intensity
            </div>
            <div style={{ display: 'flex', gap: '5px' }}>
              {([
                { id: 'subtle', label: 'Subtle', bars: 1, emoji: '○' },
                { id: 'normal', label: 'Normal', bars: 2, emoji: '◑' },
                { id: 'vivid',  label: 'Vivid',  bars: 3, emoji: '●' },
              ] as { id: BgIntensity; label: string; bars: number; emoji: string }[]).map(opt => {
                const isOn = bgIntensity === opt.id;
                return (
                  <motion.button
                    key={opt.id}
                    onClick={() => updateBgIntensity(opt.id)}
                    whileHover={{ scale: 1.05, y: -1 }}
                    whileTap={{ scale: 0.93 }}
                    animate={isOn ? { scale: 1.02 } : { scale: 1 }}
                    transition={{ type: 'spring', stiffness: 450, damping: 26 }}
                    style={{
                      flex: 1, height: 40, borderRadius: 9, cursor: 'pointer', outline: 'none',
                      background: isOn
                        ? `linear-gradient(135deg, ${primary}40, ${primary}22)`
                        : $light ? `${primary}08` : 'rgba(255,255,255,0.04)',
                      border: isOn
                        ? `2px solid ${primary}cc`
                        : $light ? `1.5px solid ${primary}20` : '2px solid rgba(255,255,255,0.08)',
                      boxShadow: isOn
                        ? `0 0 0 3px ${primary}30, 0 4px 16px ${primary}55`
                        : '0 2px 6px rgba(0,0,0,0.3)',
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', gap: 4,
                    }}
                  >
                    <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                      {[1, 2, 3].map(b => (
                        <motion.div
                          key={b}
                          animate={{
                            height: b <= opt.bars ? 4 + b * 3 : 4,
                            background: b <= opt.bars
                              ? (isOn ? primary : $light ? `${primary}55` : 'rgba(255,255,255,0.55)')
                              : $light ? `${primary}18` : 'rgba(255,255,255,0.10)',
                          }}
                          transition={{ duration: 0.25, ease: 'easeOut' }}
                          style={{ width: 3.5, borderRadius: 2 }}
                        />
                      ))}
                    </div>
                    <span style={{
                      fontSize: '0.46rem', fontWeight: 800, letterSpacing: '0.07em',
                      color: isOn ? primary : $light ? `${primary}50` : 'rgba(255,255,255,0.28)',
                      transition: 'color 180ms ease',
                    }}>
                      {opt.label.toUpperCase()}
                    </span>
                  </motion.button>
                );
              })}
            </div>

          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Bottom nav ────────────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.125rem',
          width: '100%',
          padding: '0 12px',
        }}
      >
        {profileItems.map(renderItem)}
      </div>
    </motion.nav>
  );
}
